'use client'
import { useRef, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  DialogContent,
  DialogDescription,
  DialogTitle,
  Dialog,
  DialogBody,
  DialogHeader,
} from "@/app/components/common/ui/dialog";
import UploadFileItem from '../../common/uploader/UploadFileItem';
import MonacoEditorPreview from "./MonacoEditorPreview";
import { useSettings } from "@/providers";

const ThemeOptionsData = ({ open, onOpenChange, data }: any) => {
  const parentRef = useRef<any | null>(null);
  const themeData = data?.value;
  const sectionId = data?.id;
  const { settings } = useSettings();
  const isDarkMode = settings?.themeMode === 'dark';
  const [localData, setLocalData] = useState<any>(null);

  useEffect(() => {
    setLocalData(data?.value || {});
  }, [data]);

  const renderFileSection = (title: string, files: any[] | any, key: string) => {
    const fileArray = Array.isArray(files) ? files : files ? [files] : [];

    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title"><FormattedMessage id={title} /></h3>
        </div>
        <div className="card-body">
          {fileArray.length > 0 ? (
            fileArray.map((file, idx) => (
              <UploadFileItem
                key={file?.id ?? idx}
                file={file}
                index={idx}
                isEnableMove={false}
                showCopyUrl={true}
              />
            ))
          ) : (
            <span className="text-[#B5B5C3] font-[600] text-[14.4px]">
              <FormattedMessage id="NO_FILE_AVAILABLE" />
            </span>
          )}
        </div>
      </div>
    );
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange} >
      <DialogContent className="container-fixed max-w-[auto] flex flex-col p-10 overflow-hidden [&>button]:hidden" >
        <DialogHeader className="p-0 border-0" >
          <DialogTitle />
          < DialogDescription />
          <div className="flex items-center justify-between flex-wrap grow gap-5 pb-7.5" >
            <div className="flex flex-col justify-center gap-2" >
              <h1 className="text-xl font-semibold leading-none text-gray-900" >
                <FormattedMessage id={data?.label} />
              </h1>
            </div>
            < button className="btn btn-sm btn-light" onClick={onOpenChange} >
              <FormattedMessage id="CLOSE" />
            </button>
          </div>
        </DialogHeader>

        < DialogBody className="scrollable-y py-0 mb-5 ps-0 pe-3 -me-7" ref={parentRef} >
          <div className="grid gap-7" >

            {/* HEADER FILES */}
            {sectionId === "header" && (
              <>
                <div className="flex flex-wrap gap-6">
                  <div className="flex-1 min-w-[250px]">
                    {renderFileSection("LOGO", localData?.logo, "logo")}
                  </div>
                  <div className="flex-1 min-w-[250px]">
                    {renderFileSection("FAVICON", localData?.favicon, "favicon")}
                  </div>
                </div>
                <div className="flex flex-wrap gap-6">
                  <div className="flex-1 min-w-[250px]">
                    {renderFileSection("CSS_FILES", localData?.css, "css")}
                  </div>
                  <div className="flex-1 min-w-[250px]">
                    {renderFileSection("FONT_FILES", localData?.fonts, "fonts")}
                  </div>
                  <div className="flex-1 min-w-[250px]">
                    {renderFileSection("JS_FILES", localData?.js, "js")}
                  </div>
                </div>
              </>
            )}

            {/* FOOTER FILES */}
            {sectionId === "footer" && (
              <div className="flex flex-wrap gap-6 flex-col">
                <div className=' card'>
                  <div className="card-header"><h3 className="card-title"> <FormattedMessage id="COPYRIGHT" /> </h3></div>
                  <div className='card-body'>
                    <p className="text-sm font-medium text-gray-900 truncate">{localData?.copyright || <span>-</span>}</p>
                  </div>
                </div>
                {renderFileSection("JS_FILES", localData?.js, "js")}
              </div>
            )}

            {/* CUSTOM CSS (code) */}
            {sectionId === "custom_css" && (
              <div className='pb-[30px] card'>
                <div className="card-header">
                  <h3 className="card-title"> <FormattedMessage id="CUSTOM_CSS" /> </h3>
                </div>
                <div className='card-body'>
                  < MonacoEditorPreview
                    value={themeData || ''
                    }
                    language="css"
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>
            )}

            {/* HEADER CUSTOM JS (code) */}
            {sectionId === "header_custom_js" && (
              <div className='pb-[30px] card'>
                <div className="card-header">
                  <h3 className="card-title"> <FormattedMessage id="HEADER_CUSTOM_JS_FILES" /> </h3>
                </div>
                <div className='card-body'>
                  < MonacoEditorPreview
                    value={themeData || ''
                    }
                    language="javascript"
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>
            )}

            {/* FOOTER CUSTOM JS (code) */}
            {sectionId === "footer_custom_js" && (
              <div className='pb-[30px] card'>
                <div className="card-header">
                  <h3 className="card-title"> <FormattedMessage id="FOOTER_CUSTOM_JS_FILES" /> </h3>
                </div>
                <div className='card-body'>
                  < MonacoEditorPreview
                    value={themeData || ''
                    }
                    language="javascript"
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>
            )}

          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export { ThemeOptionsData };
